import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { FormikProps } from "formik";
import React from "react";

import {
  Alert,
  Box,
  Button,
  EntryList,
  Loader,
  Radio,
  RadioGroup,
  Text,
  Textarea,
} from "~/components/blocks";

import { Fields } from "../types";
import { useResetErrors } from "../use-reset-errors";
import { Agreement } from "./Agreement";
import { questions } from "./questions";
type Props = {
  firstAccess: boolean;
  sending: boolean;
  formik: FormikProps<Fields>;
  error: string | null;
};

const Subject = styled(Text)(({ theme }) =>
  css({
    fontSize: theme.fontSizes.m,
    marginBottom: theme.space.s,
  }),
);

const Wrap = styled(Box)(({ theme }) =>
  css({
    width: "100%",
    maxWidth: theme.windowSize.base,
    margin: "0 auto",
    padding: "0",
    boxSizing: "border-box",
    [`@media ${theme.mediaQueries.sp}`]: {
      margin: `0 auto`,
    },
  }),
);

export const InputQuestionnaire = React.memo((props: Props) => {
  const { formik, sending, error } = props;
  const theme = useTheme();

  const isInputted =
    formik.values.answers.question1.value.length > 0 &&
    formik.values.answers.question2.value.length > 0 &&
    formik.values.answers.question3.value.length > 0 &&
    formik.values.answers.question4.value.length > 0 &&
    formik.values.isAgreed;

  useResetErrors(formik, props.firstAccess);

  return (
    <Wrap>
      <Loader open={sending} transparent={false} />
      {error && (
        <Alert
          status="error"
          whiteSpace="pre-wrap"
          marginBottom={theme.space.xl}
        >
          {error}
        </Alert>
      )}
      <Text block size="m" fontWeight="bold">
        お薬をお渡しする際に重要な内容になります。
        <br />
        5つの質問にお答えください
      </Text>
      <EntryList marginTop={theme.space.m}>
        <EntryList.Head>
          <Subject>{`Q1：${questions[0].subject}`}</Subject>
        </EntryList.Head>
        <EntryList.Body>
          <RadioGroup orientation="vertical">
            {questions[0].choices?.map((choice, idx) => (
              <Radio
                key={idx}
                name="answers.question1.value[0]"
                disabled={sending}
                label={choice}
                value={choice}
                checked={
                  formik.values.answers.question1.value &&
                  formik.values.answers.question1.value.includes(choice)
                }
                onChange={formik.handleChange}
              />
            ))}
          </RadioGroup>
        </EntryList.Body>
      </EntryList>
      <EntryList marginTop={theme.space.m}>
        <EntryList.Head>
          <Subject>{`Q2：${questions[1].subject}`}</Subject>
        </EntryList.Head>
        <EntryList.Body>
          <RadioGroup orientation="vertical">
            {questions[1].choices?.map((choice, idx) => (
              <Radio
                key={idx}
                name="answers.question2.value[0]"
                label={choice}
                value={choice}
                checked={
                  formik.values.answers.question2.value &&
                  formik.values.answers.question2.value.includes(choice)
                }
                onChange={formik.handleChange}
                disabled={sending}
              />
            ))}
          </RadioGroup>
        </EntryList.Body>
      </EntryList>
      <EntryList marginTop={theme.space.m}>
        <EntryList.Head>
          <Subject>{`Q3：${questions[2].subject}`}</Subject>
        </EntryList.Head>
        <EntryList.Body>
          <RadioGroup orientation="vertical">
            {questions[2].choices?.map((choice, idx) => (
              <Radio
                key={idx}
                name="answers.question3.value[0]"
                label={choice}
                value={choice}
                checked={
                  formik.values.answers.question3.value &&
                  formik.values.answers.question3.value.includes(choice)
                }
                onChange={formik.handleChange}
                disabled={sending}
              />
            ))}
          </RadioGroup>
          <Box marginTop={theme.space.m} paddingX={theme.space.s}>
            {questions[2].remarks?.map((remark, idx) => (
              <Text key={idx} size="xs" color="grey06">
                {`※${remark}`}
              </Text>
            ))}
          </Box>
        </EntryList.Body>
      </EntryList>
      <EntryList marginTop={theme.space.m}>
        <EntryList.Head>
          <Subject>{`Q4：${questions[3].subject}`}</Subject>
        </EntryList.Head>
        <EntryList.Body>
          <RadioGroup orientation="vertical">
            {questions[3].choices?.map((choice, idx) => (
              <Radio
                key={idx}
                name="answers.question4.value[0]"
                label={choice}
                value={choice}
                checked={
                  formik.values.answers.question4.value &&
                  formik.values.answers.question4.value.includes(choice)
                }
                onChange={formik.handleChange}
                disabled={sending}
              />
            ))}
          </RadioGroup>
        </EntryList.Body>
      </EntryList>
      <EntryList marginTop={theme.space.m}>
        <EntryList.Head>
          <Subject>{`Q5：${questions[4].subject}`}</Subject>
        </EntryList.Head>
        <EntryList.Body>
          <Textarea
            name="answers.question5.value[0]"
            placeholder="任意入力"
            disabled={sending}
            error={
              formik.errors.answers?.question5?.value &&
              formik.errors.answers.question5.value[0]
            }
            value={
              formik.values.answers?.question5 &&
              formik.values.answers.question5.value
            }
            onChange={(e) => {
              const newValue = e.target.value ? [e.target.value] : [];
              formik.setFieldValue("answers.question5.value", newValue);
            }}
          />
        </EntryList.Body>
      </EntryList>
      <Agreement
        disabled={sending}
        agreement={formik.values.isAgreed}
        onChange={formik.handleChange}
      />
      <Button
        use="base"
        wide="fill"
        marginTop={theme.space.xl}
        disabled={!formik.isValid || !isInputted || sending}
        onClick={formik.submitForm}
      >
        処方箋を送信する
      </Button>
    </Wrap>
  );
});

InputQuestionnaire.displayName = "InputQuestionnaire";
